import { basicSetup } from "codemirror";
import { EditorView } from "@codemirror/view";
import { EditorState } from "@codemirror/state";
import { MergeView } from "@codemirror/merge";
import { css } from "@codemirror/lang-css";
import { html } from "@codemirror/lang-html";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { xml } from "@codemirror/lang-xml";
import { StreamLanguage } from "@codemirror/language";
import { csharp } from "@codemirror/legacy-modes/mode/clike";

declare global {
    interface Window {
        App: Record<string, unknown>;
    }
}

interface DiffEditorInstance {
    mergeView: MergeView;
    originalText: string;
    modifiedText: string;
    editable: boolean;
    language: string;
}

interface DotNetHelper {
    invokeMethodAsync(methodName: string, ...args: unknown[]): Promise<unknown>;
}

const editors = new Map<string, DiffEditorInstance>();

function getLanguageExtension(language: string): any {
    switch (language) {
        case "markdown":
            return markdown();
        case "json":
            return json();
        case "html":
            return html();
        case "css":
            return css();
        case "javascript":
            return javascript();
        case "typescript":
            return javascript({ typescript: true });
        case "csharp":
            return StreamLanguage.define(csharp);
        case "xml":
            return xml();
        default:
            return [];
    }
}

function createExtensions(language: string, editable: boolean, onModifiedChanged: (value: string) => void): any[] {
    const langExt = getLanguageExtension(language);
    const extensions: any[] = [
        basicSetup,
        EditorView.lineWrapping,
        ...(Array.isArray(langExt) ? langExt : [langExt])
    ];

    if (editable) {
        extensions.push(
            EditorView.updateListener.of((update: any) => {
                if (update.docChanged) {
                    onModifiedChanged(update.state.doc.toString());
                }
            })
        );
    } else {
        extensions.push(
            EditorView.editable.of(false),
            EditorState.readOnly.of(true)
        );
    }

    return extensions;
}

function attachSyncScroll(mergeView: MergeView) {
    const left = mergeView.a.scrollDOM;
    const right = mergeView.b.scrollDOM;

    let syncInProgress = false;

    const sync = (source: HTMLElement, target: HTMLElement) => {
        if (syncInProgress) {
            return;
        }

        syncInProgress = true;
        target.scrollTop = source.scrollTop;
        target.scrollLeft = source.scrollLeft;
        syncInProgress = false;
    };

    left.addEventListener("scroll", () => sync(left, right), { passive: true });
    right.addEventListener("scroll", () => sync(right, left), { passive: true });
}

function createDiffEditor(elementId: string, original: string, modified: string, language: string, editable: boolean, height: string, dotNetHelper?: DotNetHelper) {
    const container = document.getElementById(elementId);
    if (!container) {
        return;
    }

    const existing = editors.get(elementId);
    if (existing) {
        existing.mergeView.destroy();
        editors.delete(elementId);
    }

    container.innerHTML = "";
    container.style.height = height;

    const onModifiedChanged = (value: string) => {
        if (dotNetHelper?.invokeMethodAsync) {
            void dotNetHelper.invokeMethodAsync("OnModifiedChanged", value).catch(() => undefined);
        }
    };

    const mergeView = new MergeView({
        a: {
            doc: original,
            extensions: createExtensions(language, false, onModifiedChanged)
        },
        b: {
            doc: modified,
            extensions: createExtensions(language, editable, onModifiedChanged)
        },
        parent: container,
        highlightChanges: true,
        gutter: true,
        diffConfig: {
            scanLimit: 5000,
            timeout: 1000
        }
    });

    attachSyncScroll(mergeView);

    editors.set(elementId, {
        mergeView,
        originalText: original,
        modifiedText: modified,
        editable,
        language
    });
}

function updateOriginal(elementId: string, original: string) {
    const existing = editors.get(elementId);
    if (!existing) {
        return;
    }

    if (existing.originalText === original) {
        return;
    }

    existing.mergeView.a.dispatch({
        changes: { from: 0, to: existing.mergeView.a.state.doc.length, insert: original }
    });
    existing.originalText = original;
}

function updateModified(elementId: string, modified: string) {
    const existing = editors.get(elementId);
    if (!existing) {
        return;
    }

    if (existing.modifiedText === modified) {
        return;
    }

    existing.mergeView.b.dispatch({
        changes: { from: 0, to: existing.mergeView.b.state.doc.length, insert: modified }
    });
    existing.modifiedText = modified;
}

function disposeDiffEditor(elementId: string) {
    const existing = editors.get(elementId);
    if (!existing) {
        return;
    }

    existing.mergeView.destroy();
    editors.delete(elementId);
}

// app.ts (which creates window.App) may run before or after this module inside the same esbuild
// bundle depending on the order of the entry file's imports. Attach immediately when possible and
// always re-attach on the next tick, once the whole bundle has finished executing.
const attachToApp = (): void => {
    const w = window as any;
    const app = w.App ?? (w.App = {});
    app.createDiffEditor = createDiffEditor;
    app.updateOriginal = updateOriginal;
    app.updateModified = updateModified;
    app.disposeDiffEditor = disposeDiffEditor;
};

attachToApp();
setTimeout(attachToApp, 0);

export {};
